// ABOUTME: Index page for the relation Email settings section.
// ABOUTME: Renders a tab per customizable email template type; the track & trace, pickup and supplier tabs only when they apply.

import { Box, useTheme } from "@mui/material";
import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { tokens } from "../../../../theme";
import TabView3 from "../../../../components/theme/TabView3/TabView3";
import { emailConfig } from "./emailConfig";
import { useTranslation } from "react-i18next";
import { useTopbar } from "../../../../contexts/TopbarProvider";
import { useGetRelationQuery } from "../../../../newapi/global/relation/relationSlice";
import { useGetSuppliersSelectQuery } from "../../../../newapi/global/supplier/supplierSlice";
import { useGetSettingsQuery } from "../../../../newapi/global/generalApiSlice";
import TrackAndTrace from "./views/TrackAndTrace";
import Pickup from "./views/Pickup";
import PurchaseOrder from "./views/PurchaseOrder";
import EmailSettings from "./views/EmailSettings";

const Email = () => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const { showExtraTitle, clearExtraTitle } = useTopbar();

    // The track & trace tab only matters when the relation sends those mails (its general
    // setting), the supplier order tab only when it has suppliers to mail. The pickup tab
    // is hidden for tenants that turned it off in Setup (Stockhub-only setting).
    const relationId = useSelector((state) => state.settings.environment.selectedId);
    const { data: relation, isError: relationError } = useGetRelationQuery(relationId, { skip: !relationId });
    const { data: suppliers, isError: suppliersError } = useGetSuppliersSelectQuery(relationId, { skip: !relationId });
    const showTrackAndTrace = !!relation?.sendTrackAndTraceEmail;
    const showPurchaseOrder = (suppliers ?? []).length > 0;
    const { data: pickupEmailSettings, isError: pickupEmailError } = useGetSettingsQuery("relation_pickup_email_disabled");
    const showPickup = !pickupEmailSettings?.some((s) => s.key === "relation_pickup_email_disabled" && s.value === "true");
    // Tabs are matched to views by position, so the config is only built once all
    // answers are in (a failed request counts as answered).
    const tabsReady = (relation !== undefined || relationError) && (suppliers !== undefined || suppliersError) && (pickupEmailSettings !== undefined || pickupEmailError);
    const tabConfig = useMemo(() => ({
        ...(showTrackAndTrace ? { trackandtrace: emailConfig.trackandtrace } : {}),
        ...(showPickup ? { pickup: emailConfig.pickup } : {}),
        ...(showPurchaseOrder ? { purchaseorder: emailConfig.purchaseorder } : {}),
        settings: emailConfig.settings,
    }), [showTrackAndTrace, showPickup, showPurchaseOrder]);

    useEffect(() => {
        showExtraTitle(t("email"))

        return () => {
            clearExtraTitle();
        };
    }, [showExtraTitle, clearExtraTitle, t, i18n.language])

    return (
        <Box
            sx={{
                background:"",
                width:1,
                paddingTop:9,
                position:"relative",
                "@media screen and (max-width:47.9375em)" : {
                    background:"",
                    width:1,
                    marginTop:9,
                    height: "calc(100% - 72px)",
                    paddingX:4,
                    paddingTop:4,
                    overflow:"hidden",
                    display:"flex",
                    flexDirection:"column",
                },
                "@media screen and (max-width: 47.9375em)" : {
                    paddingX:0,
                    paddingTop:0,
                    background:colors.bg["tertiary"],
                    "&:before,&:after":{
                        display:"none"
                    }
                },
            }}
        >
            {tabsReady && <TabView3
                tabConfig={tabConfig}
                sx={{
                    minHeight:"calc(100%)",
                    background: `${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                    position:"relative",
                    zIndex:2,
                    display:"flex",
                    flexDirection:"column",
                    "&:before" : {
                        content: "''",
                        display:"block",
                        width:"32px",
                        height:"32px",
                        position:"absolute",
                        left:0,
                        top:"40px",
                        zIndex:1,
                        backgroundColor: `${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                        height:"48px",
                        width:"24px",
                        borderTopLeftRadius:"24px",
                        boxShadow: `0 -24px 0 0 ${colors.bg["tertiary"]}`,
                        transition:"250ms"
                    },
                    "&:after" : {
                        content: "''",
                        display:"block",
                        width:"32px",
                        height:"32px",
                        position:"absolute",
                        top:"40px",
                        zIndex:1,
                        backgroundColor: `${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                        right:0,
                        height:"48px",
                        width:"24px",
                        borderTopLeftRadius:"24px",
                        boxShadow: `0 -24px 0 0 ${colors.bg["tertiary"]}`,
                        transform:"scaleX(-1)",
                        transition:"250ms"
                    },
                    "@media screen and (max-width: 47.9375em)" : {
                        "&:before, &:after" : {
                            top:0,
                        }
                    }
                }}
            >
                <>
                    {showTrackAndTrace && <TrackAndTrace config={emailConfig.trackandtrace}/>}
                    {showPickup && <Pickup config={emailConfig.pickup}/>}
                    {showPurchaseOrder && <PurchaseOrder config={emailConfig.purchaseorder}/>}
                    <EmailSettings config={emailConfig.settings}/>
                </>
            </TabView3>}
        </Box>
    )
};

export default Email;
